import { useMemo, useState } from 'react';
import type { ReviewPair } from '@/types/insights';

interface NetworkGraphProps {
  pairs: ReviewPair[];
  height?: number;
}

interface Node {
  id: string;
  label: string;
  avatarUrl?: string;
  x: number;
  y: number;
  angle: number;
  totalReviews: number;
}

interface Link {
  source: Node;
  target: Node;
  value: number;
}

export default function NetworkGraph({ pairs, height = 600 }: NetworkGraphProps) {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Process data to create nodes and links with layout
  const { nodes, links } = useMemo(() => {
    // 1. Identify all unique developers involved in the pairs
    const devIds = new Set<string>();
    const devMap = new Map<string, { label: string; avatarUrl?: string; totalReviews: number }>();

    // Helper to add dev info
    const addDev = (id: string, login: string, name?: string, avatar?: string) => {
      devIds.add(id);
      if (!devMap.has(id)) {
        devMap.set(id, {
          label: name || login,
          avatarUrl: avatar,
          totalReviews: 0
        });
      }
    };

    pairs.forEach(p => {
      addDev(p.reviewer_id, p.reviewer_login, p.reviewer_name, p.reviewer_avatar);
      addDev(p.author_id, p.author_login, p.author_name, p.author_avatar);
      
      const reviewer = devMap.get(p.reviewer_id);
      const author = devMap.get(p.author_id);
      if (reviewer) reviewer.totalReviews += p.review_count;
      if (author) author.totalReviews += p.review_count;
    });

    // 2. Sort nodes by activity (total reviews) to put most active ones in a predictable order
    const sortedDevIds = Array.from(devIds).sort((a, b) => {
      return (devMap.get(b)?.totalReviews || 0) - (devMap.get(a)?.totalReviews || 0);
    });

    // Limit to top N nodes to avoid overcrowding if needed, but for now let's try all
    // Circular Layout Config
    const width = 800; // Internal coordinate system width
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 60; // Padding for labels

    // 3. Create Nodes with positions
    const nodesMap = new Map<string, Node>();
    const nodeList: Node[] = sortedDevIds.map((id, index) => {
      const angle = (index / sortedDevIds.length) * 2 * Math.PI - Math.PI / 2; // Start from top
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      const info = devMap.get(id)!;
      
      const node: Node = {
        id,
        label: info.label,
        avatarUrl: info.avatarUrl,
        totalReviews: info.totalReviews,
        x,
        y,
        angle
      };
      nodesMap.set(id, node);
      return node;
    });

    // 4. Create Links
    const linkList: Link[] = pairs.map(p => ({
      source: nodesMap.get(p.reviewer_id)!,
      target: nodesMap.get(p.author_id)!,
      value: p.review_count
    })).filter(l => l.source && l.target); // Safety check

    return { nodes: nodeList, links: linkList };
  }, [pairs, height]);

  // Interaction handlers
  const isNodeDimmed = (nodeId: string) => {
    if (!hoveredNode) return false;
    if (hoveredNode === nodeId) return false;
    // Check if connected
    return !links.some(l => 
      (l.source.id === hoveredNode && l.target.id === nodeId) ||
      (l.target.id === hoveredNode && l.source.id === nodeId)
    );
  };

  const isLinkDimmed = (link: Link) => {
    if (!hoveredNode) return false;
    return link.source.id !== hoveredNode && link.target.id !== hoveredNode;
  };

  // Find max value for scaling line width
  const maxLinkValue = Math.max(...links.map(l => l.value), 1);

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
        No network data to display
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm py-5">
      <svg 
        viewBox={`0 0 800 ${height}`} 
        className="w-full h-full max-h-[600px]" 
        style={{ maxWidth: '100%' }}
      >
        <defs>
          {/* Gradients for interesting visuals */}
          <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="var(--tw-gradient-from, #60a5fa)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--tw-gradient-to, #3b82f6)" stopOpacity="0.1" />
          </radialGradient>
        </defs>

        {/* Links */}
        <g>
          {links.map((link, i) => {
            const isDimmed = isLinkDimmed(link);
            const isHovered = hoveredNode && (link.source.id === hoveredNode || link.target.id === hoveredNode);
            
            // Quadratic Bezier curve control point (center of circle) to make lines curved inwards
            // Or simple straight lines. For circular layout, straight lines or slightly curved inwards looks good.
            // Let's do quadratic curve towards center (400, height/2) with some dampening
            const cx = 400;
            const cy = height / 2;
            const mx = (link.source.x + link.target.x) / 2;
            const my = (link.source.y + link.target.y) / 2;
            const qx = (cx + mx) / 2;
            const qy = (cy + my) / 2;

            // Stroke width based on value
            const strokeWidth = Math.max(1, (link.value / maxLinkValue) * 8);

            return (
              <path
                key={`${link.source.id}-${link.target.id}-${i}`}
                d={`M ${link.source.x} ${link.source.y} Q ${qx} ${qy} ${link.target.x} ${link.target.y}`}
                fill="none"
                stroke={isHovered ? '#3b82f6' : '#94a3b8'}
                strokeWidth={strokeWidth}
                strokeOpacity={isDimmed ? 0.1 : (isHovered ? 0.8 : 0.4)}
                className="transition-all duration-300 ease-in-out"
                style={{ pointerEvents: 'none' }} // Let clicks pass through to nodes/container
              />
            );
          })}
        </g>

        {/* Nodes */}
        <g>
          {nodes.map((node) => {
            const isDimmed = isNodeDimmed(node.id);
            const isHovered = hoveredNode === node.id;
            const baseSize = 25; // Increased base size for better mobile visibility
            const size = baseSize + Math.min(node.totalReviews, 25); // Dynamic size based on activity, max size reduced to maintain reasonable proportions
            const clippedRadius = size - 2; // Radius of the visible clipped area
            const avatarDiameter = clippedRadius * 2 - 4; // Avatar image will be 4px smaller than clipped area
            const avatarOffset = (clippedRadius * 2 - avatarDiameter) / 2; // Offset to center the smaller avatar

            return (
              <g 
                key={node.id} 
                className="cursor-pointer transition-opacity duration-300"
                style={{ opacity: isDimmed ? 0.3 : 1 }}
                onMouseEnter={() => setHoveredNode(node.id)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                {/* Node Glow/Halo on hover */}
                {isHovered && (
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={size + 10}
                    fill="url(#nodeGradient)"
                    className="animate-pulse"
                  />
                )}

                {/* Node Background */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={size}
                  fill={isHovered ? '#eff6ff' : '#ffffff'}
                  stroke={isHovered ? '#3b82f6' : '#e2e8f0'}
                  strokeWidth={isHovered ? 3 : 2}
                  className="transition-all duration-300 dark:fill-gray-800 dark:stroke-gray-600"
                />

                {/* Avatar Mask - SVG way */}
                <defs>
                  <clipPath id={`clip-${node.id}`}>
                    <circle cx={node.x} cy={node.y} r={clippedRadius} />
                  </clipPath>
                </defs>
                {node.avatarUrl ? (
                  <image
                    href={node.avatarUrl}
                    x={node.x - clippedRadius + avatarOffset}
                    y={node.y - clippedRadius + avatarOffset}
                    width={avatarDiameter}
                    height={avatarDiameter}
                    clipPath={`url(#clip-${node.id})`}
                  />
                ) : (
                  // Fallback initials
                  <text
                    x={node.x}
                    y={node.y}
                    dy=".35em"
                    textAnchor="middle"
                    className="font-bold text-gray-400 dark:text-gray-500 pointer-events-none select-none"
                    fontSize={size * 0.7} // Reduced font size to provide more padding
                  >
                    {node.label.charAt(0).toUpperCase()}
                  </text>
                )}

                {/* Label */}
                <text
                  x={node.x}
                  y={node.y + size + 20}
                  textAnchor="middle"
                  className={`text-sm font-medium fill-gray-700 dark:fill-gray-200 pointer-events-none select-none ${isHovered ? 'font-bold' : ''}`}
                  style={{ 
                    textShadow: '0 1px 2px rgba(255,255,255,0.8)',
                    opacity: isHovered || !hoveredNode ? 1 : 0 // Hide label when dimmed
                  }}
                >
                  {node.label}
                </text>
                
                {/* Tooltip-like details on hover */}
                {isHovered && (
                   <text
                   x={node.x}
                   y={node.y + size + 35}
                   textAnchor="middle"
                   className="text-xs fill-gray-500 dark:fill-gray-400 pointer-events-none select-none"
                 >
                   {node.totalReviews} reviews involved
                 </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
